import {
  addOrientationChangeListener,
  getOrientationAsync,
  Orientation,
} from "@-/com.npmjs.expo-screen-orientation";
import { Platform } from "@-/com.npmjs.react-native";
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from "@-/com.npmjs.react-native-safe-area-context";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { getDeviceEdgeSafeAreaInsets, type SensorHousingEdge } from "#device-edge-safe-area-insets";

function getSensorHousingEdge(orientation: Orientation): SensorHousingEdge | null {
  // Expo mirrors `UIInterfaceOrientation`: `landscapeLeft` puts the home side on the left, so the
  // top of the device (and its sensor housing) sits on the right.
  if (orientation === Orientation.LANDSCAPE_LEFT) {
    return "right";
  }
  if (orientation === Orientation.LANDSCAPE_RIGHT) {
    return "left";
  }
  return null;
}

function useIosInterfaceOrientation() {
  const [orientation, setOrientation] = useState(Orientation.UNKNOWN);

  useEffect(() => {
    if (Platform.OS !== "ios") {
      return;
    }
    let hasOrientationEvent = false;
    // A 180° landscape flip keeps the window size and the symmetric insets unchanged, so only the
    // orientation event reveals which edge the sensor housing moved to.
    const subscription = addOrientationChangeListener((event) => {
      hasOrientationEvent = true;
      setOrientation(event.orientationInfo.orientation);
    });
    void getOrientationAsync().then((initialOrientation) => {
      if (!hasOrientationEvent) {
        setOrientation(initialOrientation);
      }
    });
    return () => {
      hasOrientationEvent = true;
      subscription.remove();
    };
  }, []);

  return orientation;
}

/**
 * Overrides `SafeAreaInsetsContext` so every `useSafeAreaInsets()` consumer only clears the
 * landscape edge that actually holds the sensor housing. Android already reports per-edge
 * display-cutout insets, so the override is iOS-only.
 *
 * Native `SafeAreaView` reads UIKit's symmetric insets directly and bypasses this context; pad
 * horizontal edges from `useSafeAreaInsets()` instead.
 */
export function DeviceEdgeSafeAreaInsetsProvider({ children }: { children: ReactNode }) {
  const insets = useSafeAreaInsets();
  const orientation = useIosInterfaceOrientation();
  const sensorHousingEdge = getSensorHousingEdge(orientation);
  const deviceEdgeInsets = useMemo(
    () => getDeviceEdgeSafeAreaInsets(insets, sensorHousingEdge),
    [insets, sensorHousingEdge],
  );

  return <SafeAreaInsetsContext value={deviceEdgeInsets}>{children}</SafeAreaInsetsContext>;
}
